import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Logo from '../Logo';
import ProfileAvatar from '../ProfileAvatar';
import { useAuth } from '../../contexts/AuthContext';
import usePendingApprovalsCount from '../../hooks/usePendingApprovalsCount';
import {
  HomeIcon,
  FolderIcon,
  BookOpenIcon,
  ChartBarIcon,
  CogIcon,
  ChevronDownIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import { FEATURES } from '../../config/phases';

// Section 19 supersedes 6.1's trimmed Navbar (no auth/admin/model selector):
// this is the main app's Navbar shape with the Admin dropdown role-gated, minus
// the model selector and organizations, which are out of scope here (D25).
const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
  { name: 'Projects', href: '/projects', icon: FolderIcon },
];

// Entries whose route does not exist yet are filtered out rather than rendered dead:
// /admin/metrics is P8, and until App.js defines it, clicking the item silently returned the
// admin to /dashboard via the catch-all. src/config/phases.js is the one place that says
// what has shipped.
const adminNavigation = [
  { name: 'Standards', href: '/standards', icon: BookOpenIcon, shipped: true },
  { name: 'Metrics & logs', href: '/admin/metrics', icon: ChartBarIcon, shipped: FEATURES.adminMetrics },
  { name: 'Criteria', href: '/criteria', icon: BookOpenIcon, shipped: true },
  // Anshul, 2026-09-26: admin-only account management (create/reset/delete auditors).
  { name: 'Users', href: '/admin/users', icon: UsersIcon, shipped: true },
].filter((item) => item.shipped);

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [adminDropdownOpen, setAdminDropdownOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const adminDropdownRef = useRef(null);
  const isAdmin = user?.role === 'ADMIN';

  // Anshul, 2026-09-26: the Users nav item shows a small count badge for admins
  // when a self-registered request is waiting. Polled modestly (every 60 s) and
  // refetched on window focus (usePendingApprovalsCount), same query key UsersPage
  // invalidates after an approve/reject so the badge does not lag its own action.
  const pendingCount = usePendingApprovalsCount({ enabled: isAdmin });
  const hasPendingApprovals = isAdmin && pendingCount > 0;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (adminDropdownRef.current && !adminDropdownRef.current.contains(event.target)) {
        setAdminDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isNavItemActive = (href) =>
    location.pathname === href || location.pathname.startsWith(`${href}/`);
  const isAdminRouteActive = adminNavigation.some((item) => isNavItemActive(item.href));

  return (
    <nav className="bg-white shadow-sm border-b border-gray-200 fixed w-full top-0 z-50 print:hidden">
      <div className="w-full min-w-0 max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex">
            <div className="flex-shrink-0 flex items-center">
              <Link to="/dashboard" className="mt-2">
                <Logo size="default" variant="full" />
              </Link>
            </div>

            <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
              {navigation.map((item) => {
                const active = isNavItemActive(item.href);
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`${
                      active
                        ? 'border-primary-500 text-gray-900'
                        : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                    } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-colors duration-200`}
                  >
                    <item.icon className="w-4 h-4 mr-2" />
                    {item.name}
                  </Link>
                );
              })}

              {isAdmin && (
                <div className="relative inline-flex" ref={adminDropdownRef}>
                  <button
                    type="button"
                    onClick={() => setAdminDropdownOpen((open) => !open)}
                    className={`${
                      isAdminRouteActive
                        ? 'border-primary-500 text-gray-900'
                        : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                    } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-colors duration-200 h-16`}
                  >
                    <CogIcon className="w-4 h-4 mr-2" />
                    Admin
                    {hasPendingApprovals && (
                      <span
                        className="ml-1.5 inline-flex items-center justify-center h-4 min-w-[1rem] px-1 rounded-full bg-red-600 text-white text-[10px] font-semibold leading-none"
                        aria-label={`${pendingCount} account request${pendingCount === 1 ? '' : 's'} waiting for approval`}
                      >
                        {pendingCount}
                      </span>
                    )}
                    <ChevronDownIcon
                      className={`w-4 h-4 ml-1 transition-transform duration-200 ${adminDropdownOpen ? 'rotate-180' : ''}`}
                    />
                  </button>

                  {adminDropdownOpen && (
                    <div className="absolute left-0 top-full mt-0 w-48 bg-white rounded-md shadow-lg py-1 border border-gray-200 z-50">
                      {adminNavigation.map((item) => {
                        const active = location.pathname === item.href;
                        const showBadge = item.name === 'Users' && hasPendingApprovals;
                        return (
                          <Link
                            key={item.name}
                            to={item.href}
                            onClick={() => setAdminDropdownOpen(false)}
                            className={`${
                              active ? 'bg-primary-50 text-primary-700' : 'text-gray-700 hover:bg-[#F8F8FF]'
                            } flex items-center px-4 py-2 text-sm transition-colors duration-200`}
                          >
                            <item.icon className="w-4 h-4 mr-3" />
                            {item.name}
                            {showBadge && (
                              <span className="ml-auto inline-flex items-center justify-center h-4 min-w-[1rem] px-1 rounded-full bg-red-600 text-white text-[10px] font-semibold leading-none">
                                {pendingCount}
                              </span>
                            )}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="hidden sm:ml-6 sm:flex sm:items-center space-x-4">
            <span className="text-sm text-gray-700">Welcome, {user?.first_name}</span>
            {/* QA-46 (axe link-name, serious, every signed-in page): the avatar's initials
                are aria-hidden, so without this the link had no accessible name. Same
                wording ProfileAvatar uses for its own photo alt text. */}
            <Link
              to="/profile"
              className="text-gray-500 hover:text-gray-700 transition-colors duration-200"
              aria-label={user?.first_name ? `${user.first_name}'s profile` : 'Profile'}
            >
              <ProfileAvatar user={user} className="w-6 h-6" />
            </Link>
            <button
              type="button"
              onClick={handleLogout}
              className="text-gray-500 hover:text-gray-700 transition-colors duration-200"
              aria-label="Sign out"
            >
              <ArrowRightOnRectangleIcon className="w-6 h-6" />
            </button>
          </div>

          <div className="sm:hidden flex items-center">
            <button
              type="button"
              onClick={() => setIsOpen((open) => !open)}
              className="text-gray-500 hover:text-gray-700 hover:bg-[#F8F8FF] p-2 rounded-md"
              aria-label={isOpen ? 'Close menu' : 'Open menu'}
            >
              {isOpen ? <XMarkIcon className="w-6 h-6" /> : <Bars3Icon className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {isOpen && (
        <div className="sm:hidden">
          <div className="pt-2 pb-3 space-y-1">
            {navigation.map((item) => {
              const active = isNavItemActive(item.href);
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setIsOpen(false)}
                  className={`${
                    active
                      ? 'bg-primary-50 border-primary-500 text-primary-700'
                      : 'border-transparent text-gray-500 hover:bg-[#F8F8FF] hover:border-gray-300 hover:text-gray-700'
                  } block pl-3 pr-4 py-2 border-l-4 text-base font-medium`}
                >
                  <div className="flex items-center">
                    <item.icon className="w-5 h-5 mr-3" />
                    {item.name}
                  </div>
                </Link>
              );
            })}

            {isAdmin && (
              <div className="border-t border-gray-200 mt-3 pt-3">
                <div className="px-3 pb-2">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Admin</p>
                </div>
                {adminNavigation.map((item) => {
                  const active = location.pathname === item.href;
                  const showBadge = item.name === 'Users' && hasPendingApprovals;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setIsOpen(false)}
                      className={`${
                        active
                          ? 'bg-primary-50 border-primary-500 text-primary-700'
                          : 'border-transparent text-gray-500 hover:bg-[#F8F8FF] hover:border-gray-300 hover:text-gray-700'
                      } block pl-3 pr-4 py-2 border-l-4 text-base font-medium`}
                    >
                      <div className="flex items-center">
                        <item.icon className="w-5 h-5 mr-3" />
                        {item.name}
                        {showBadge && (
                          <span className="ml-2 inline-flex items-center justify-center h-4 min-w-[1rem] px-1 rounded-full bg-red-600 text-white text-[10px] font-semibold leading-none">
                            {pendingCount}
                          </span>
                        )}
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>

          <div className="pt-4 pb-3 border-t border-gray-200">
            <div className="flex items-center px-4">
              <div className="flex-shrink-0 mr-3">
                <ProfileAvatar user={user} className="w-10 h-10" />
              </div>
              <div>
                <div className="text-base font-medium text-gray-800">
                  {user?.first_name} {user?.last_name}
                </div>
                <div className="text-sm text-gray-500">{user?.email}</div>
              </div>
            </div>
            <div className="mt-3 space-y-1">
              <Link
                to="/profile"
                onClick={() => setIsOpen(false)}
                className="block px-4 py-2 text-base font-medium text-gray-500 hover:text-gray-800 hover:bg-[#F8F8FF]"
              >
                Profile
              </Link>
              <button
                type="button"
                onClick={() => {
                  handleLogout();
                  setIsOpen(false);
                }}
                className="block w-full text-left px-4 py-2 text-base font-medium text-gray-500 hover:text-gray-800 hover:bg-[#F8F8FF]"
              >
                <div className="flex items-center">
                  <ArrowRightOnRectangleIcon className="w-5 h-5 mr-3" />
                  Sign out
                </div>
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
